import React, { Fragment, useCallback, } from "react";
import { v4 as uuidv4 } from "uuid";
import { useFormContext } from "../Form";
import { observer } from "mobx-react-lite";
import { Checkbox as BSCheckbox } from "react-bootstrap";
import { ensureObservableValue } from "../withFormWrapper";
import { StaticMultipleValues } from "../StaticInput";
import { useSelectionAreaClasses } from "../styling/SelectionArea.styles";
import { classNames } from "../utils/classNames";
import { useCheckboxGroupClasses } from "./CheckboxGroup.styles";
import { useUtilityStyles } from "../hooks/useUtilityStyles";
const RawCheckboxGroup = observer(({ children, label, options, onChange, name, disabled, staticFormatter, id, dataId, dataTestId, dataLabel, selectionDirection, styleOptions, }) => {
    const labelId = id || uuidv4();
    const { values, formDisabled, isStatic } = useFormContext();
    if (name) {
        ensureObservableValue(values, name);
        if (!values.get(name)) {
            values.set(name, []);
        }
    }
    const value = values.get(name);
    const handleChange = useCallback((e, option) => {
        const mappedExistingValues = value.map((v) => v);
        const index = value.indexOf(option);
        const checked = e.target.checked;
        if (checked && index === -1) {
            value.push(option);
        }
        else if (!checked && index !== -1) {
            value.splice(index, 1);
        }
        if (onChange) {
            onChange(value, mappedExistingValues);
        }
        values.set(name, value);
    }, [value, onChange, values, name]);
    if (isStatic && name && options) {
        const multiple = true;
        return (React.createElement(StaticMultipleValues, { name: name, label: label, staticFormatter: staticFormatter, options: options, multiple: multiple, dataId: dataId, dataTestId: dataTestId, dataLabel: dataLabel }));
    }
    return (React.createElement(Container, { styleOptions: styleOptions, dataId: dataId, labelId: labelId, dataTestId: dataTestId },
        React.createElement(Label, { label: label, labelId: labelId }),
        children && !options && children,
        !selectionDirection && options && (React.createElement(CheckboxStandard, { options: options, dataId: dataId, dataLabel: dataLabel, dataTestId: dataTestId, value: value, disabled: formDisabled || disabled, handleChange: handleChange })),
        selectionDirection && options && (React.createElement(Group, { selectionDirection: selectionDirection }, options?.map((option, i) => {
            const checked = value.indexOf(option.value) !== -1;
            const optionDisabled = formDisabled || disabled || option.disabled;
            return (React.createElement(SelectionArea, { checked: checked, disabled: optionDisabled, key: option.value, last: i + 1 === options.length, first: i === 0, label: option.label },
                React.createElement(Checkbox, { option: option, value: value, disabled: optionDisabled, onChange: handleChange, checked: checked })));
        })))));
});
const CheckboxStandard = observer(({ options, dataId, dataTestId, dataLabel, value, disabled, handleChange, }) => {
    return (React.createElement(Fragment, null, options?.map((option) => {
        return (React.createElement(BSCheckbox, { "data-id": dataId ? `${dataId}-${option.value}` : undefined, "data-testid": dataTestId ? `${dataTestId}-${option.value}` : undefined, "data-label": dataLabel ? `${dataLabel} ${option.value}` : undefined, key: option.value, name: option.value, checked: value.indexOf(option.value) !== -1, onChange: (e) => handleChange(e, option.value), disabled: disabled || option.disabled, "aria-labelledby": `label-${option.value}` },
            React.createElement("span", { id: `label-${option.value}` }, option.label)));
    })));
});
const Checkbox = ({ option, disabled, onChange, checked }) => {
    const classes = useSelectionAreaClasses({});
    const { input } = useCheckboxGroupClasses();
    return (React.createElement("label", { className: classNames(classes.selectionLabel, disabled ? classes.selectionLabelDisabled : undefined) },
        React.createElement("span", { className: classes.contentBlock }, option.label),
        React.createElement("input", { name: option.value, disabled: disabled, checked: checked, type: "checkbox", onChange: (e) => onChange(e, option.value), className: input })));
};
const Label = (props) => (React.createElement("span", { className: "form-control-static-label", id: props?.labelId ? `title-${props?.labelId}` : undefined }, props?.label));
const Container = (props) => {
    const classes = useUtilityStyles(props?.styleOptions);
    return (React.createElement("div", { "data-id": props?.dataId, "data-testid": props?.dataTestId, className: classNames("checkbox-group", classes.utilities), role: "group", "aria-labelledby": props?.labelId ? `title-${props?.labelId}` : undefined }, props?.children));
};
const Group = (props) => {
    const classes = useSelectionAreaClasses({});
    return (React.createElement("div", { className: classes.selectionAreaGroup, "data-hook": "selection-area-group" }, props?.children));
};
const SelectionArea = observer((props) => {
    const { checked, disabled } = props;
    const { selectionArea, selectionAreaChecked, selectionAreaDisabled, selectionAreaCheckedDisabled, } = useSelectionAreaClasses({
        last: props?.last,
        first: props?.first,
    });
    return (React.createElement("div", { "data-checked": checked ? "true" : "false", "data-disabled": disabled ? "true" : "false", "data-label": props?.label, className: classNames(selectionArea, checked ? selectionAreaChecked : undefined, disabled ? selectionAreaDisabled : undefined, checked && disabled ? selectionAreaCheckedDisabled : undefined) }, props?.children));
});
export const CheckboxGroup = Object.assign(RawCheckboxGroup, {
    SelectionArea,
    Group,
    Checkbox,
    Container,
    Label,
});
